import { useMemo } from 'react';
import { DataSourcesQueries } from '@frontend/api-data-sources';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { GlobalBannerView } from '@frontend/global-info-banner';
import { useTranslation } from '@frontend/i18n';
import { useGetAppointmentColorsEnabled } from '@frontend/schedule-helpers';
import { useModalControl } from '@frontend/design-system';
import { AppointmentTypesV3TrackingIds } from '../../trackingIds/appointment-types-v3-trackingids';
import { AppointmentTypesBulkColorAssignModal } from '../appointment-types-bulk-color-assign-modal';

interface Props {
  isCalendarView?: boolean;
  // For calendar view, we can have multiple locations selected
  locationIds?: string[];
}

export const AppointmentTypeBulkColorAssignmentBanner = ({ isCalendarView = false, locationIds }: Props) => {
  const { t } = useTranslation('schedule');
  const { isIntegratedOffice } = DataSourcesQueries.useGetAccessibleIntegrations();
  const modalControl = useModalControl();
  const { canUseAppointmentColors } = useGetAppointmentColorsEnabled();

  const { data: appointmentTypesData, isLoading } = SchedulerV3.Helpers.useGetAppointmentTypesV3Data({
    selectedLocationId: locationIds?.[0] ?? '',
    selectedLocationIds: locationIds ?? [],
    pageConfig: {
      page: 1,
      limit: SchedulerV3.Constants.CALENDAR_VIEW_V3_API_QUERY_LIMIT,
    },
    isEnabled: canUseAppointmentColors,
  });
  const appointmentTypes = useMemo(
    () =>
      (appointmentTypesData?.appointmentTypes ?? [])
        .slice()
        .sort((a, b) => (a.displayName ?? '').localeCompare(b.displayName ?? '')),
    [appointmentTypesData]
  );

  const dismissTimezoneBanner = () => {
    // TODO: Implement dismissal logic
  };
  const assignColorsClickHandler = () => {
    // TODO: Implement click handler
    modalControl.openModal();
  };

  const title = isIntegratedOffice ? t('Color Migration Assistant') : t('Add Colors to Your Appointment Types');
  const subTitle = isIntegratedOffice
    ? t('Automatically map your existing appointment type colors to our new enhanced color system')
    : t('Make your calendar easier to scan by assigning colors to your appointment types');
  const actionLabel = isIntegratedOffice ? t('Map Colors') : t('Assign Colors');

  if (!canUseAppointmentColors || isLoading) return null;

  const isColorCustomized = appointmentTypes.some((type) => type.details?.clr);
  if (!appointmentTypes.length || isColorCustomized) return null;

  return (
    <>
      <AppointmentTypesBulkColorAssignModal
        {...modalControl.modalProps}
        isCalendarView={isCalendarView}
        shouldShowLocationColumn={(locationIds ?? []).length > 1}
        appointmentTypes={appointmentTypes}
      />
      <GlobalBannerView
        type='info'
        title={title}
        message={subTitle}
        isDismissible={isCalendarView}
        onDismiss={dismissTimezoneBanner}
        trackingId={AppointmentTypesV3TrackingIds.bulkAssignmentBanner.assignColorsBtn}
        action={{
          label: actionLabel,
          onClick: assignColorsClickHandler,
        }}
      />
    </>
  );
};
